<script lang="ts">
	import { browser } from '$app/environment';
	import {
		cfgAppMode,
		cfgAppName,
		cfgBrand,
		cfgBrandTitle,
		cfgSupportChatApiUrl,
		cfgTheme
	} from '@infinitro/config';
	import { Verbiage, t } from '@infinitro/content';
	import {
		isMobile,
		loggedIn,
		locale,
		stateSupportChatOpen,
		stateUsername
	} from '@infinitro/global';
	import { cubicOut } from 'svelte/easing';
	import { onMount, tick } from 'svelte';
	import { scale } from 'svelte/transition';
	import { supportChatDebug, supportChatWarn } from './debug';
	import SupportChatArticleView from './parts/support-chat-article-view.svelte';
	import SupportChatHeader from './parts/support-chat-header.svelte';
	import SupportChatHelp from './parts/support-chat-help.svelte';
	import SupportChatHome from './parts/support-chat-home.svelte';
	import SupportChatLauncher from './parts/support-chat-launcher.svelte';
	import SupportChatMessages from './parts/support-chat-messages.svelte';
	import SupportChatTabbar from './parts/support-chat-tabbar.svelte';
	import {
		bootstrapSupportChatSession,
		ensureDefaultSupportConversation,
		isTerminalSupportConversationStatus,
		loadMoreMessagesForConversation,
		openSupportConversation,
		planSupportMessageSendSteps,
		refreshSupportConversations,
		sendUserMessage,
		stateConversationHasMore,
		stateLoadingMoreMessages,
		stateSupportChatThreads,
		stopSupportChatRealtime,
		stateSupportChatMessageMaxBytes,
		supportChatMessageByteLength
	} from './support-chat-session';
	import type { ArticleTreeNode, SupportChatMessageRow, Tab } from './types';
	import {
		findArticleLeafBySlug,
		loadSupportChatArticleTree,
		resolveSupportChatHomeLeaves,
		type FeaturedArticleLeaf
	} from './utils/support-chat-article-tree';

	const defaultMessageThreadId = 'support-general';
	const brandTitle = cfgBrandTitle();

	$: supportChatApiConfigured = Boolean(cfgSupportChatApiUrl()?.trim());

	async function refreshArticleTree() {
		if (!browser) return;
		try {
			const manifest = await loadSupportChatArticleTree($locale);
			articleTreeRoots = manifest.nodes;
			if (articleView) {
				articleView = findArticleLeafBySlug(manifest.nodes, articleView.articleSlug) ?? articleView;
			}
			helpTreeNonce += 1;
		} catch {
			supportChatWarn('Support chat article tree reload failed');
		}
	}

	onMount(() => {
		supportChatDebug('Widget mount', {
			appName: cfgAppName(),
			appMode: cfgAppMode(),
			theme: cfgTheme(),
			brand: cfgBrand(),
			supportChatApiConfigured,
			apiUrl: cfgSupportChatApiUrl() ?? ''
		});
		let unsubLoggedIn: (() => void) | null = null;
		let unsubLocale: (() => void) | null = null;
		if (supportChatApiConfigured) {
			void bootstrapSupportChatSession();
		}
		if (browser) {
			unsubLoggedIn = loggedIn.subscribe((v) => {
				if (!Boolean(cfgSupportChatApiUrl()?.trim())) return;
				if (v === undefined) return;
				void bootstrapSupportChatSession();
			});
			unsubLocale = locale.subscribe(() => {
				void refreshArticleTree();
			});
		}
		return () => {
			unsubLoggedIn?.();
			unsubLocale?.();
			supportChatDebug('Widget unmount');
			if (Boolean(cfgSupportChatApiUrl()?.trim())) stopSupportChatRealtime();
		};
	});

	/**
	 * When true (player/public shell), visibility is driven by `stateSupportChatOpen` and the
	 * floating launcher is not rendered — same entry point as Intercom via sidebar support.
	 */
	export let shellEmbed = false;

	/** Shell: each time the user opens support from the app chrome, sync conversation list from API. */
	$: if (browser && supportChatApiConfigured && shellEmbed && $stateSupportChatOpen) {
		void refreshSupportConversations();
	}

	let activeTab: Tab = 'home';
	let helpSearch = '';
	let homeQuestion = '';
	let messageQuestion = '';
	let messageGifUrl = '';
	let selectedImageFile: File | null = null;
	let lastSubmittedQuestion: string[] = [];
	let internalIsOpen = true;
	let homeQuestionInput: HTMLInputElement | null = null;
	let messageQuestionInput: HTMLInputElement | null = null;
	let selectedMessageThreadId: string | null = null;
	let optimisticMessages: SupportChatMessageRow[] = [];
	let isSendingMessage = false;

	let articleTreeRoots: ArticleTreeNode[] = [];
	let helpTreeNonce = 0;
	let articleView: FeaturedArticleLeaf | null = null;

	$: isWidgetOpen = shellEmbed ? $stateSupportChatOpen : internalIsOpen;

	/**
	 * Live byte cap for the composer counter; reacts as soon as the guest/player actor resolves
	 * (see `stateSupportChatActorKind`). The authoritative check re-runs in `sendUserMessage`.
	 */
	$: messageMaxContentBytes = $stateSupportChatMessageMaxBytes;
	$: homeQuestionOverLimit =
		supportChatMessageByteLength(homeQuestion.trim()) > messageMaxContentBytes;
	$: messageQuestionOverLimit =
		supportChatMessageByteLength(messageQuestion.trim()) > messageMaxContentBytes;

	/** Intercom-style: grow/shrink from launcher corner (see `.widget-shell` transform-origin). */
	$: widgetScale = {
		duration: $isMobile ? 300 : 260,
		start: 0.88,
		opacity: 0,
		easing: cubicOut
	};

	$: visitorName = $stateUsername || brandTitle;
	$: normalizedHelpSearch = helpSearch.trim().toLowerCase();
	$: featuredHomeLeaves = resolveSupportChatHomeLeaves(articleTreeRoots);
	$: hasConversation = lastSubmittedQuestion.some((q) => q.trim().length > 0);
	$: hideMainChrome = activeTab === 'messages' && selectedMessageThreadId !== null;

	function openWidget() {
		if (shellEmbed) {
			stateSupportChatOpen.set(true);
		} else {
			internalIsOpen = true;
		}
		if (supportChatApiConfigured) void refreshSupportConversations();
	}

	function closeWidget() {
		if (shellEmbed) {
			stateSupportChatOpen.set(false);
		} else {
			internalIsOpen = false;
		}
		articleView = null;
	}

	function switchTab(tab: Tab) {
		articleView = null;
		activeTab = tab;

		if (tab !== 'help') {
			helpSearch = '';
		}
		if (tab !== 'messages') {
			selectedMessageThreadId = null;
		}
	}

	async function focusInput(tab: Tab) {
		switchTab(tab);
		await tick();

		if (tab === 'messages') {
			messageQuestionInput?.focus();
			return;
		}

		homeQuestionInput?.focus();
	}

	async function submitQuestion(source: 'home' | 'messages') {
		const text = (source === 'home' ? homeQuestion : messageQuestion).trim();
		const gif = source === 'messages' ? messageGifUrl.trim() : '';
		const imageFile = source === 'messages' ? selectedImageFile : null;
		const question = text || gif || (imageFile ? ' ' : '');
		supportChatDebug('Submit question', {
			source,
			supportChatApiConfigured,
			questionLength: question.trim().length,
			hasGif: Boolean(gif),
			hasImage: Boolean(imageFile)
		});

		if (supportChatMessageByteLength(text) > messageMaxContentBytes) {
			supportChatWarn('Submit question skipped: message too long', {
				source,
				maxBytes: messageMaxContentBytes
			});
			return;
		}

		if (!text && !gif && !imageFile) {
			supportChatWarn('Submit question skipped: empty question', { source });
			void focusInput('messages');
			return;
		}

		homeQuestion = '';
		messageQuestion = '';
		messageGifUrl = '';
		const pendingImageFile = imageFile;
		selectedImageFile = null;
		activeTab = 'messages';

		const steps = planSupportMessageSendSteps(text, gif, pendingImageFile);

		if (supportChatApiConfigured) {
			isSendingMessage = true;
			const activeThread =
				$stateSupportChatThreads.find((t) => !isTerminalSupportConversationStatus(t.status)) ??
				$stateSupportChatThreads[0];
			selectedMessageThreadId =
				(selectedMessageThreadId &&
				!isTerminalSupportConversationStatus(
					$stateSupportChatThreads.find((t) => t.id === selectedMessageThreadId)?.status
				)
					? selectedMessageThreadId
					: null) ??
				activeThread?.id ??
				defaultMessageThreadId;

			// All steps shown pending upfront: the text step's await can take a while (a synchronous
			// AI reply rides the same response), and the GIF shouldn't wait behind that visually.
			optimisticMessages = steps.map((step) => ({
				role: 'user',
				body: step.body,
				status: 'pending'
			}));

			let allStepsSucceeded = true;
			for (let i = 0; i < steps.length; i++) {
				const step = steps[i];
				const cid = await sendUserMessage(step.body, { imageFile: step.imageFile });
				if (!cid) {
					allStepsSucceeded = false;
					const unsentBodies = new Set(steps.slice(i).map((s) => s.body));
					optimisticMessages = optimisticMessages.map((m) =>
						unsentBodies.has(m.body) ? { ...m, status: 'failed' } : m
					);
					supportChatWarn('Submit question API failed, showing error state', {
						body: step.body
					});
					break;
				}
				selectedMessageThreadId = cid;
				supportChatDebug('Submit question API path complete', { selectedMessageThreadId });
			}
			if (allStepsSucceeded) optimisticMessages = [];
			isSendingMessage = false;
			lastSubmittedQuestion = [];
		} else {
			supportChatWarn('Submit question fell back to mock path', {
				apiUrl: cfgSupportChatApiUrl() ?? ''
			});
			lastSubmittedQuestion = steps.map((step) => step.body);
			selectedMessageThreadId = defaultMessageThreadId;
		}
	}

	function openHelpSearch(prefillSearch = '') {
		articleView = null;
		activeTab = 'help';
		helpSearch = prefillSearch;
	}

	function openArticle(leaf: FeaturedArticleLeaf) {
		articleView = leaf;
	}

	function handleHelpLeaf(node: ArticleTreeNode) {
		const slug = node.articleSlug?.trim();
		if (!slug) return;
		articleView = { id: node.id, title: node.title, articleSlug: slug };
	}

	function closeArticleView() {
		articleView = null;
	}

	function handleHomeInputKeydown(event: KeyboardEvent) {
		if (event.key !== 'Enter') return;
		event.preventDefault();
		void submitQuestion('home');
	}

	function handleMessageInputKeydown(event: KeyboardEvent) {
		if (event.key !== 'Enter') return;
		event.preventDefault();
		void submitQuestion('messages');
	}

	async function openDefaultConversation() {
		activeTab = 'messages';
		if (supportChatApiConfigured) {
			const cid = await ensureDefaultSupportConversation();
			selectedMessageThreadId = cid ?? defaultMessageThreadId;
			if (cid) void openSupportConversation(cid);
		} else {
			selectedMessageThreadId = defaultMessageThreadId;
		}
		await focusInput('messages');
	}
</script>

{#if isWidgetOpen}
	<section
		class:widget-shell-mobile={$isMobile}
		class="widget-shell"
		aria-label={$t(Verbiage.support)}
		transition:scale={widgetScale}
	>
		<div class="widget-card" class:widget-card-article={articleView}>
			{#if !hideMainChrome}
				<SupportChatHeader onClose={closeWidget} {activeTab} />
			{/if}

			<div class="widget-body">
				{#if articleView}
					<SupportChatArticleView
						articleTitle={articleView.title}
						articleSlug={articleView.articleSlug}
						onBack={closeArticleView}
					/>
				{:else if activeTab === 'home'}
					<SupportChatHome
						featuredLeaves={featuredHomeLeaves}
						{brandTitle}
						bind:inputElement={homeQuestionInput}
						bind:question={homeQuestion}
						{visitorName}
						contentTooLong={homeQuestionOverLimit}
						onInputKeydown={handleHomeInputKeydown}
						onSearchArticles={() => openHelpSearch()}
						onSubmitQuestion={() => void submitQuestion('home')}
						onSelectArticle={openArticle}
					/>
				{:else if activeTab === 'messages'}
					<SupportChatMessages
						{brandTitle}
						hasConversation={hasConversation && !supportChatApiConfigured}
						bind:inputElement={messageQuestionInput}
						bind:question={messageQuestion}
						bind:messageGifUrl
						bind:selectedImageFile
						bind:selectedThreadId={selectedMessageThreadId}
						chatThreads={$stateSupportChatThreads}
						{lastSubmittedQuestion}
						{optimisticMessages}
						{isSendingMessage}
						contentTooLong={messageQuestionOverLimit}
						isLoadingMore={selectedMessageThreadId
							? ($stateLoadingMoreMessages[selectedMessageThreadId] ?? false)
							: false}
						hasMore={selectedMessageThreadId
							? ($stateConversationHasMore[selectedMessageThreadId] ?? false)
							: false}
						onLoadMoreMessages={loadMoreMessagesForConversation}
						onFocusInput={() => focusInput('messages')}
						onInputKeydown={handleMessageInputKeydown}
						onSubmitQuestion={() => void submitQuestion('messages')}
						onThreadOpen={(id) => void openSupportConversation(id)}
						onStartNewConversation={() => void openDefaultConversation()}
						onClose={closeWidget}
					/>
				{:else}
					{#key helpTreeNonce}
						<SupportChatHelp
							articleRoots={articleTreeRoots}
							normalizedSearch={normalizedHelpSearch}
							bind:search={helpSearch}
							onOpenLeaf={handleHelpLeaf}
						/>
					{/key}
				{/if}
			</div>

			{#if !hideMainChrome}
				<SupportChatTabbar {activeTab} onSwitchTab={switchTab} />
			{/if}
		</div>
	</section>
{:else if !shellEmbed}
	<SupportChatLauncher onOpen={openWidget} />
{/if}

<style>
	.widget-shell {
		position: fixed;
		bottom: 1.5rem;
		right: 1.5rem;
		z-index: 1200;
		transform-origin: bottom right;
}

	.widget-shell-mobile {
		inset: 0px;
		display: flex;
		min-height: 0px;
		flex-direction: column;
		overflow: hidden;
		height: calc(var(--vh, 1vh) * 100);
		height: var(--app-vh, 100dvh);
		height: 100dvh;
		max-height: calc(var(--vh, 1vh) * 100);
		max-height: var(--app-vh, 100dvh);
		max-height: 100dvh;
		transform-origin: bottom right;
}

	.widget-card {
		display: flex;
		flex-direction: column;
		overflow: hidden;
		border-radius: 28px;
		width: min(390px, calc(100vw - 24px));
		height: min(720px, calc(var(--vh, 1vh) * 100 - 48px));
		height: min(720px, calc(var(--app-vh, 100dvh) - 48px));
		height: min(720px, calc(100dvh - 48px));
		max-height: min(720px, calc(var(--vh, 1vh) * 100 - 48px));
		max-height: min(720px, calc(var(--app-vh, 100dvh) - 48px));
		max-height: min(720px, calc(100dvh - 48px));
		background: linear-gradient(
			180deg,
			var(--color-secondary-700) 0%,
			var(--color-secondary-500) 100%
		);
		box-shadow:
			0 24px 60px rgba(var(--color-background-900-rgb), 0.4),
			0 0 0 1px rgba(var(--color-text-100-rgb), 0.08);
		transition:
			width 220ms ease,
			height 220ms ease,
			max-height 220ms ease,
			border-radius 220ms ease;
}

	.widget-card-article {
		width: min(560px, calc(100vw - 24px));
		height: min(780px, calc(var(--vh, 1vh) * 100 - 48px));
		height: min(780px, calc(var(--app-vh, 100dvh) - 48px));
		height: min(780px, calc(100dvh - 48px));
		max-height: min(780px, calc(var(--vh, 1vh) * 100 - 48px));
		max-height: min(780px, calc(var(--app-vh, 100dvh) - 48px));
		max-height: min(780px, calc(100dvh - 48px));
	}

	.widget-shell-mobile .widget-card {
		max-height: 100%;
		min-height: 0px;
		width: 100%;
		flex: 1 1 0%;
		border-radius: 0px;
		box-shadow: none;
}

	.widget-body {
		min-height: 0px;
		flex: 1 1 0%;
		overflow: hidden;
}
</style>
